import React, { useState, useEffect } from 'react';
import { FileText, Clock, CheckCircle2, XCircle, Upload, Eye, RefreshCw, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { documentRequestService, portfolioService } from '@/lib/api';
import ActaZipUploader from '@/components/ActaZipUploader';

const DocumentRequestsManager = () => {
  const [requests, setRequests] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterPortfolio, setFilterPortfolio] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [responseNotes, setResponseNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPortfolio]);

  const loadData = async () => {
    try {
      const [requestsRes, portfoliosRes] = await Promise.all([
        documentRequestService.list({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          portfolio_id: filterPortfolio !== 'all' ? filterPortfolio : undefined
        }),
        portfolioService.list()
      ]);
      
      setRequests(requestsRes.data || []);
      setPortfolios(portfoliosRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (request) => {
    setSelectedRequest(request);
    setResponseNotes('');
    setUploadFile(null);
    setShowDetailModal(true);
  };

  const handleUploadDocument = async () => {
    if (!selectedRequest || !uploadFile) {
      toast.error('Selecciona un archivo');
      return;
    }
    
    setUploading(true);
    try {
      await documentRequestService.upload(selectedRequest.id, uploadFile);
      toast.success('Documento subido y solicitud completada');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    try {
      await documentRequestService.update(selectedRequest.id, {
        status: 'rejected',
        response_notes: responseNotes
      });
      toast.success('Solicitud rechazada');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('Error al rechazar solicitud');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.debtor_name?.toLowerCase().includes(search) ||
      req.debtor_dni?.toLowerCase().includes(search) ||
      req.domain?.toLowerCase().includes(search) ||
      req.acta_number?.toLowerCase().includes(search)
    );
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>;
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700">Completada</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rechazada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'acta': return 'Acta';
      case 'factura': return 'Factura';
      case 'comprobante': return 'Comprobante';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ActaZipUploader portfolios={portfolios} />
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Solicitudes de Documentos
          </h2>
          <p className="text-slate-600">Gestiona las solicitudes de actas y facturas de los clientes</p>
        </div>
        
        {pendingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            <span className="text-amber-800 font-medium">
              {pendingCount} solicitud(es) pendiente(s)
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm">Buscar</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre, DNI, Dominio..."
                className="pl-9"
              />
            </div>
          </div>
          
          <div className="w-[180px]">
            <Label className="text-sm">Estado</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="completed">Completadas</SelectItem>
                <SelectItem value="rejected">Rechazadas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-[200px]">
            <Label className="text-sm">Cartera</Label>
            <Select value={filterPortfolio} onValueChange={setFilterPortfolio}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las carteras</SelectItem>
                {portfolios.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </Card>

      {/* Requests List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Dominio/Acta</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Cartera</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-slate-50">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-slate-900">{request.debtor_name}</p>
                      <p className="text-sm text-slate-500">{request.debtor_dni || '-'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <Badge variant="outline">{getTypeLabel(request.request_type)}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      {request.domain && <p>Dominio: <span className="font-mono">{request.domain}</span></p>}
                      {request.acta_number && <p>Acta: <span className="font-mono">{request.acta_number}</span></p>}
                      {!request.domain && !request.acta_number && <span className="text-slate-400">-</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-slate-600">{request.portfolio_name || '-'}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-slate-600">
                      {new Date(request.created_at).toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDetail(request)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      {request.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleOpenDetail(request)}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Responder
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredRequests.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {filterStatus === 'pending' 
                  ? 'No hay solicitudes pendientes'
                  : 'No hay solicitudes que coincidan con los filtros'}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Detail/Response Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Solicitud de {getTypeLabel(selectedRequest?.request_type)}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.status === 'pending' 
                ? 'Sube el documento solicitado para completar esta solicitud'
                : 'Detalle de la solicitud'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Cliente:</span>
                  <span className="font-medium">{selectedRequest.debtor_name}</span>
                </div>
                {selectedRequest.debtor_dni && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">DNI:</span>
                    <span className="font-mono">{selectedRequest.debtor_dni}</span>
                  </div>
                )}
                {selectedRequest.domain && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Dominio:</span>
                    <span className="font-mono">{selectedRequest.domain}</span>
                  </div>
                )}
                {selectedRequest.acta_number && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Nº Acta:</span>
                    <span className="font-mono">{selectedRequest.acta_number}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Cartera:</span>
                  <span>{selectedRequest.portfolio_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Solicitado:</span>
                  <span>{new Date(selectedRequest.created_at).toLocaleString('es-AR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Estado:</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
              </div>
              
              {selectedRequest.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Notas del solicitante:</strong> {selectedRequest.notes}
                  </p>
                </div>
              )}
              
              {/* Completed request - show document */}
              {selectedRequest.status === 'completed' && selectedRequest.document_url && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-800 mb-2">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                    Documento adjuntado
                  </p>
                  <a 
                    href={selectedRequest.document_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${selectedRequest.document_url}` : selectedRequest.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline text-sm"
                    data-testid="document-request-view-file"
                  >
                    Ver documento
                  </a>
                </div>
              )}
              
              {/* Rejected request */}
              {selectedRequest.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    <XCircle className="w-4 h-4 inline mr-1" />
                    Solicitud rechazada
                  </p>
                  {selectedRequest.response_notes && (
                    <p className="text-sm text-red-700 mt-1">
                      Motivo: {selectedRequest.response_notes}
                    </p>
                  )}
                </div>
              )}
              
              {/* Pending - show upload form */}
              {selectedRequest.status === 'pending' && (
                <>
                  <div className="space-y-3">
                    <Label>Subir documento</Label>
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    />
                    {uploadFile && (
                      <p className="text-sm text-slate-600">
                        Archivo seleccionado: {uploadFile.name}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notas de respuesta (opcional)</Label>
                    <Textarea
                      value={responseNotes}
                      onChange={(e) => setResponseNotes(e.target.value)}
                      placeholder="Agregar notas sobre el documento..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={handleReject}
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                    <Button
                      onClick={handleUploadDocument}
                      disabled={!uploadFile || uploading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {uploading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Completar Solicitud
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentRequestsManager;
